import { useState, useMemo, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";

import dayjs from "dayjs";

import { Button } from "@/components/ui/button";
import TabsComponent from "@/components/tabs/tabs-component";
import type { TabItemProps } from "@/components/tabs/tab-component.type";
import {
  useFilterBuilder,
  useSortBuilder,
  type FilterableAttribute,
  type SortableAttribute,
  AttributeDataType,
  serializeFiltersToQuery,
  serializeSortsToQuery
} from "@/components/search-filter-bar";
import { useDebounce } from "@/hooks/use-debounce";
import { useScheduleCalendar } from "@/hooks/use-schedule-calendar";

import { useGetData } from "@/hooks/services/use-query-hooks";
import {
  GetJobScheduleByJob,
  GetJobScheduleByTechnician,
  GetJobScheduleByMachine
} from "@/hooks/services/pps/handlers/job-schedule-api";
import { TechnicianList } from "@/hooks/services/pps/handlers/technicians-api";
import { MachineListGroupedByShop } from "@/hooks/services/pps/handlers/machines-api";
import {
  JobScheduleItem,
  TechnicianScheduleItem,
  MachineScheduleItem
} from "@/hooks/services/pps/types/job-schedule.type";
import {
  ScheduledJob,
  JobAssignment,
  TechnicianScheduleGantt,
  MachineScheduleGantt,
  ViewMode
} from "@/hooks/services/pps/handlers/schedule/job-schedule-types";
import { useWorkSpaceStore } from "@/stores/store";
import { WorkspaceToggle } from "@/components/workspace-toggle";

import JobGanttChart from "./components/job-schedule";
import TechnicianGanttChart from "./components/technician-schedule";
import MachineGanttChart from "./components/machine-schedule";
import AiPlanningDrawer from "./components/ai-planning-drawer";
import { ZOOM_MIN, ZOOM_MAX, ZOOM_STEP } from "./utils";

export const Route = createFileRoute("/plant-3/job-scheduler/")({
  component: RouteComponent
});

const toBarVariant = (value: unknown): "red" | "green" | undefined =>
  value === "red" || value === "green" ? value : undefined;

const getBarVariantFromSource = (
  source: unknown
): "red" | "green" | undefined => {
  const raw = source as any;
  return toBarVariant(raw?.barVariant ?? raw?.BarVariant);
};

const getBarUnavailableReasonFromSource = (source: unknown): string | null => {
  const raw = source as any;
  const reason = raw?.barUnavailableReason ?? raw?.BarUnavailableReason;
  return typeof reason === "string" && reason.trim().length > 0 ? reason : null;
};


// Simple transform: just format dates to time
const transformJobScheduleToAssignment = (
  item: JobScheduleItem,
  index: number
): JobAssignment => ({
  id: `assign-${item.jobOrderId}-${index}`,
  assignmentId: `${item.jobOrderId}-${index}`,
  jobOrderNo: item.jobOrderNo,
  jobOrderId: item.jobOrderId,
  processId: 0,
  processCode: 0,
  processDesc: item.processName || "",
  partNo: item.partNumber || "",
  partDescription: item.partDescription || "",
  quantity: 0,
  standardHours: item.durationHours ?? 0,
  // Keep full datetime for calculations
  startDate: item.startDate || "",
  eddDate: item.eddDate || "",
  duration: item.durationHours ?? 0,
  technicianId: null,
  technicianName: item.technicianName,
  machineId: null,
  machineName: item.machineName,
  statusId: 0,
  status: item.status || "scheduled",
  barVariant: getBarVariantFromSource(item),
  barUnavailableReason: getBarUnavailableReasonFromSource(item),
  priority: item.priority ?? 1,
  notes: null
});

// Transform backend data to ScheduledJob[] for job-centric view
const transformToScheduledJobs = (items: JobScheduleItem[]): ScheduledJob[] => {
  const jobsMap = new Map<number, ScheduledJob>();
  const insertionOrder: number[] = [];

  items.forEach((item, index) => {
    if (!jobsMap.has(item.jobOrderId)) {
      insertionOrder.push(item.jobOrderId);
      jobsMap.set(item.jobOrderId, {
        id: item.jobOrderId,
        jobOrderNo: item.jobOrderNo,
        jobOrderId: item.jobOrderId,
        partNo: item.partNumber || "",
        partDescription: item.partDescription || "",
        customerName: item.customerName || "",
        totalQuantity: 0,
        overallStartDate: item.startDate || "",
        overallEddDate: item.eddDate || "",
        assignments: []
      });
    }

    const job = jobsMap.get(item.jobOrderId)!;
    job.assignments.push(transformJobScheduleToAssignment(item, index));

    // Update overall date range
    const currentStart = item.startDate || "";
    const currentEnd = item.eddDate || "";

    if (
      currentStart &&
      (!job.overallStartDate || currentStart < job.overallStartDate)
    ) {
      job.overallStartDate = currentStart;
    }
    if (
      currentEnd &&
      (!job.overallEddDate || currentEnd > job.overallEddDate)
    ) {
      job.overallEddDate = currentEnd;
    }
  });

  return insertionOrder.map((jobOrderId) => jobsMap.get(jobOrderId)!);
};

// Transform backend TechnicianScheduleItem to frontend TechnicianScheduleGantt
const transformToTechnicianSchedule = (
  items: TechnicianScheduleItem[]
): TechnicianScheduleGantt[] => {
  const techMap = new Map<number, TechnicianScheduleGantt>();
  const insertionOrder: number[] = [];

  items.forEach((item, index) => {
    const raw = item as any;
    const technicianId = raw.technicianId ?? raw.TechnicianId;
    const technicianName = raw.technicianName ?? raw.TechnicianName;
    const shopName = raw.shopName ?? raw.ShopName ?? "";

    if (technicianId == null) {
      return;
    }

    if (!techMap.has(technicianId)) {
      insertionOrder.push(technicianId);
      techMap.set(technicianId, {
        shopName,
        technicianId,
        technicianName,
        assignedJobs: []
      });
    }

    const startDateRaw =
      raw.startDate ?? raw.StartDate ?? raw.startTime ?? raw.StartTime ?? "";
    // Backend already resolves EDD ?? EstimatedEndDate into eddDate/endTime (WOR-2187).
    const resolvedEndDate =
      raw.eddDate ?? raw.EddDate ?? raw.endTime ?? raw.EndTime ?? startDateRaw;

    const assignment: JobAssignment = {
      id: `tech-${technicianId}-${raw.jobOrderId ?? raw.JobOrderId}-${index}`,
      assignmentId: `${technicianId}-${raw.jobOrderId ?? raw.JobOrderId}-${index}`,
      jobOrderNo: raw.jobOrderNo ?? raw.JobOrderNo,
      jobOrderId: raw.jobOrderId ?? raw.JobOrderId,
      processId: 0,
      processCode: 0,
      processDesc: raw.processName ?? raw.ProcessName ?? "",
      partNo: raw.partNumber ?? raw.PartNumber ?? "",
      partDescription: raw.partDescription ?? raw.PartDescription ?? "",
      quantity: 0,
      standardHours: raw.durationHours ?? raw.DurationHours ?? 0,
      startDate: startDateRaw,
      eddDate: resolvedEndDate,
      duration: raw.durationHours ?? raw.DurationHours ?? 0,
      technicianId,
      technicianName,
      machineId: null,
      machineName: raw.machineName ?? raw.MachineName,
      statusId: 0,
      status: raw.status ?? raw.Status ?? "scheduled",
      barVariant: getBarVariantFromSource(raw),
      barUnavailableReason: getBarUnavailableReasonFromSource(raw),
      priority: raw.priority ?? raw.Priority ?? 1,
      notes: null,
      customerName: raw.customerName ?? raw.CustomerName
    };

    techMap.get(technicianId)!.assignedJobs.push(assignment);
  });

  return insertionOrder.map((technicianId) => techMap.get(technicianId)!);
};

const transformToMachineSchedule = (
  items: MachineScheduleItem[]
): MachineScheduleGantt[] => {
  const machineMap = new Map<number, MachineScheduleGantt>();
  const insertionOrder: number[] = [];

  // Map machineStatus number to string
  const getMachineStatus = (
    status: number
  ): "available" | "maintenance" | "unavailable" => {
    switch (status) {
      case 1:
        return "available";
      case 2:
        return "maintenance";
      case 3:
        return "unavailable";
      default:
        return "available";
    }
  };

  items.forEach((item, index) => {
    const raw = item as any;
    const machineId = raw.machineId ?? raw.MachineId;
    const machineName = raw.machineName ?? raw.MachineName;
    const shopName = raw.shopName ?? raw.ShopName ?? "";
    const machineStatus = raw.machineStatus ?? raw.MachineStatus;

    if (machineId == null) {
      return;
    }

    if (!machineMap.has(machineId)) {
      insertionOrder.push(machineId);
      machineMap.set(machineId, {
        machineId,
        machineName,
        shopName,
        status: getMachineStatus(machineStatus),
        assignedJobs: []
      });
    }

    const startDateRaw =
      raw.startDate ?? raw.StartDate ?? raw.startTime ?? raw.StartTime ?? "";
    // Backend already resolves EDD ?? EstimatedEndDate into eddDate/endTime (WOR-2187).
    const resolvedEndDate =
      raw.eddDate ?? raw.EddDate ?? raw.endTime ?? raw.EndTime ?? startDateRaw;

    const assignment: JobAssignment = {
      id: `machine-${machineId}-${raw.jobOrderId ?? raw.JobOrderId}-${index}`,
      assignmentId: `${machineId}-${raw.jobOrderId ?? raw.JobOrderId}-${index}`,
      jobOrderNo: raw.jobOrderNo ?? raw.JobOrderNo,
      jobOrderId: raw.jobOrderId ?? raw.JobOrderId,
      processId: 0,
      processCode: 0,
      processDesc: raw.processName ?? raw.ProcessName ?? "",
      partNo: raw.partNumber ?? raw.PartNumber ?? "",
      partDescription: raw.partDescription ?? raw.PartDescription ?? "",
      quantity: 0,
      standardHours: raw.durationHours ?? raw.DurationHours ?? 0,
      startDate: startDateRaw,
      eddDate: resolvedEndDate,
      duration: raw.durationHours ?? raw.DurationHours ?? 0,
      technicianId: null,
      technicianName: raw.technicianName ?? raw.TechnicianName,
      machineId,
      machineName,
      statusId: 0,
      status: raw.status ?? raw.Status ?? "scheduled",
      barVariant: getBarVariantFromSource(raw),
      barUnavailableReason: getBarUnavailableReasonFromSource(raw),
      priority: raw.priority ?? raw.Priority ?? 1,
      notes: null,
      customerName: raw.customerName ?? raw.CustomerName
    };

    machineMap.get(machineId)!.assignedJobs.push(assignment);
  });

  return insertionOrder.map((machineId) => machineMap.get(machineId)!);
};

const fallbackTechnicianItemsFromJobs = (
  items: JobScheduleItem[]
): TechnicianScheduleItem[] => {
  const technicianIds = new Map<string, number>();
  let nextId = 1;

  return items
    .filter((item) => !!item.technicianName)
    .map((item) => {
      const key = item.technicianName || "Unassigned";
      if (!technicianIds.has(key)) {
        technicianIds.set(key, nextId++);
      }

      return {
        technicianId: technicianIds.get(key)!,
        technicianName: key,
        employeeNumber: null,
        shopName: null,
        jobOrderId: item.jobOrderId,
        jobOrderNo: item.jobOrderNo,
        partNumber: item.partNumber,
        partDescription: item.partDescription,
        customerName: item.customerName,
        processName: item.processName,
        machineName: item.machineName,
        startTime: item.startDate,
        endTime: item.eddDate,
        durationHours: item.durationHours,
        priority: item.priority,
        status: item.status,
        barVariant: getBarVariantFromSource(item),
        barUnavailableReason: getBarUnavailableReasonFromSource(item)
      };
    });
};

const fallbackMachineItemsFromJobs = (
  items: JobScheduleItem[]
): MachineScheduleItem[] => {
  const machineIds = new Map<string, number>();
  let nextId = 1;

  return items
    .filter((item) => !!item.machineName)
    .map((item) => {
      const key = item.machineName || "Unknown Machine";
      if (!machineIds.has(key)) {
        machineIds.set(key, nextId++);
      }

      return {
        machineId: machineIds.get(key)!,
        machineName: key,
        machineNumber: machineIds.get(key)!,
        shopName: null,
        machineStatus: 1,
        jobOrderId: item.jobOrderId,
        jobOrderNo: item.jobOrderNo,
        partNumber: item.partNumber,
        partDescription: item.partDescription,
        customerName: item.customerName,
        processName: item.processName,
        technicianName: item.technicianName,
        startTime: item.startDate,
        endTime: item.eddDate,
        startDate: item.startDate,
        eddDate: item.eddDate,
        durationHours: item.durationHours,
        priority: item.priority,
        status: item.status,
        barVariant: getBarVariantFromSource(item),
        barUnavailableReason: getBarUnavailableReasonFromSource(item)
      };
    });
};

const buildTechnicianScheduleFromMasterAndJobs = (
  technicians: any[],
  jobs: JobScheduleItem[]
): TechnicianScheduleGantt[] => {
  const byName = new Map<string, any>();
  technicians.forEach((tech) => {
    const name = String(tech?.name ?? "").trim();
    if (!name) return;
    byName.set(name.toLowerCase(), tech);
  });

  const scheduleMap = new Map<number, TechnicianScheduleGantt>();
  technicians.forEach((tech) => {
    scheduleMap.set(tech.id, {
      technicianId: tech.id,
      technicianName: tech.name,
      shopName: tech?.shop?.shopName || "",
      assignedJobs: []
    });
  });

  const unknownTechMap = new Map<string, number>();
  let nextUnknownTechId = 900000;

  jobs.forEach((job, index) => {
    if (!job.technicianName) return;

    const key = String(job.technicianName).trim().toLowerCase();
    const master = byName.get(key);
    let technicianId: number;
    let technicianName: string;

    if (master) {
      technicianId = master.id;
      technicianName = master.name;
    } else {
      if (!unknownTechMap.has(key)) {
        unknownTechMap.set(key, nextUnknownTechId++);
      }
      technicianId = unknownTechMap.get(key)!;
      technicianName = job.technicianName;
      if (!scheduleMap.has(technicianId)) {
        scheduleMap.set(technicianId, {
          technicianId,
          technicianName,
          shopName: "",
          assignedJobs: []
        });
      }
    }

    const assignment: JobAssignment = {
      id: `tech-master-${technicianId}-${job.jobOrderId}-${index}`,
      assignmentId: `${technicianId}-${job.jobOrderId}-${index}`,
      jobOrderNo: job.jobOrderNo,
      jobOrderId: job.jobOrderId,
      processId: 0,
      processCode: 0,
      processDesc: job.processName || "",
      partNo: job.partNumber || "",
      partDescription: job.partDescription || "",
      quantity: 0,
      standardHours: job.durationHours ?? 0,
      startDate: job.startDate || "",
      eddDate: job.eddDate || "",
      duration: job.durationHours ?? 0,
      technicianId,
      technicianName,
      machineId: null,
      machineName: job.machineName,
      statusId: 0,
      status: job.status || "scheduled",
      barVariant: getBarVariantFromSource(job),
      barUnavailableReason: getBarUnavailableReasonFromSource(job),
      priority: job.priority ?? 1,
      notes: null,
      customerName: job.customerName
    };

    scheduleMap.get(technicianId)?.assignedJobs.push(assignment);
  });

  return Array.from(scheduleMap.values());
};

const buildMachineScheduleFromMasterAndJobs = (
  machines: any[],
  jobs: JobScheduleItem[]
): MachineScheduleGantt[] => {
  const byName = new Map<string, any>();
  machines.forEach((machine) => {
    const name = String(machine?.name ?? "").trim();
    if (!name) return;
    byName.set(name.toLowerCase(), machine);
  });

  const toMachineStatus = (
    status: number
  ): "available" | "maintenance" | "unavailable" => {
    switch (status) {
      case 2:
        return "maintenance";
      case 3:
        return "unavailable";
      default:
        return "available";
    }
  };

  const scheduleMap = new Map<number, MachineScheduleGantt>();
  machines.forEach((machine) => {
    scheduleMap.set(machine.id, {
      machineId: machine.id,
      machineName: machine.name,
      shopName: machine?.shop?.shopName || "",
      status: toMachineStatus(machine?.status?.id ?? 1),
      assignedJobs: []
    });
  });

  const unknownMachineMap = new Map<string, number>();
  let nextUnknownMachineId = 800000;

  jobs.forEach((job, index) => {
    if (!job.machineName) return;

    const key = String(job.machineName).trim().toLowerCase();
    const master = byName.get(key);
    let machineId: number;
    let machineName: string;

    if (master) {
      machineId = master.id;
      machineName = master.name;
    } else {
      if (!unknownMachineMap.has(key)) {
        unknownMachineMap.set(key, nextUnknownMachineId++);
      }
      machineId = unknownMachineMap.get(key)!;
      machineName = job.machineName;
      if (!scheduleMap.has(machineId)) {
        scheduleMap.set(machineId, {
          machineId,
          machineName,
          shopName: "",
          status: "available",
          assignedJobs: []
        });
      }
    }

    const assignment: JobAssignment = {
      id: `machine-master-${machineId}-${job.jobOrderId}-${index}`,
      assignmentId: `${machineId}-${job.jobOrderId}-${index}`,
      jobOrderNo: job.jobOrderNo,
      jobOrderId: job.jobOrderId,
      processId: 0,
      processCode: 0,
      processDesc: job.processName || "",
      partNo: job.partNumber || "",
      partDescription: job.partDescription || "",
      quantity: 0,
      standardHours: job.durationHours ?? 0,
      startDate: job.startDate || "",
      eddDate: job.eddDate || "",
      duration: job.durationHours ?? 0,
      technicianId: null,
      technicianName: job.technicianName,
      machineId,
      machineName,
      statusId: 0,
      status: job.status || "scheduled",
      barVariant: getBarVariantFromSource(job),
      barUnavailableReason: getBarUnavailableReasonFromSource(job),
      priority: job.priority ?? 1,
      notes: null,
      customerName: job.customerName
    };

    scheduleMap.get(machineId)?.assignedJobs.push(assignment);
  });

  return Array.from(scheduleMap.values());
};

function RouteComponent() {
  const [activeTab, setActiveTab] = useState("job");
  const [showTechnicianWithJobsOnly, _setShowTechnicianWithJobsOnly] =
    useState(false);
  const [showMachineWithJobsOnly, _setShowMachineWithJobsOnly] =
    useState(false);
  const [isPlanningDrawerOpen, setIsPlanningDrawerOpen] = useState(false);
  const { workspace } = useWorkSpaceStore();

  const [viewMode, setViewMode] = useState<ViewMode>("weekly");
  const [zoomLevel, setZoomLevel] = useState<number>(30);
  const onZoomIn = () => setZoomLevel((z) => Math.min(z + ZOOM_STEP, ZOOM_MAX));
  const onZoomOut = () =>
    setZoomLevel((z) => Math.max(z - ZOOM_STEP, ZOOM_MIN));
  const { dateRange, setDateRange } = useScheduleCalendar("weekly");

  useEffect(() => {
    if (activeTab === "job") {
      setZoomLevel(20);
    } else {
      setZoomLevel(30);
    }
  }, [activeTab]);

  // Search, Filter, and Sort state
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const { filters, addFilter, updateFilter, removeFilter, clearFilters } =
    useFilterBuilder();
  const { sorts, addSort, updateSort, removeSort, clearSorts } =
    useSortBuilder();
  const debouncedFilters = useDebounce(filters, 500);
  const debouncedSorts = useDebounce(sorts, 500);

  // Memoize query params. Always expand to full calendar months so that switching
  // between Day / Week / Month views within the same month reuses cached data.
  // For multi-month date-picker ranges, both the start month (from dateRange.from)
  // and the end month (from dateRange.to) are fully included.
  const queryParams = useMemo(
    () => ({
      workspaceId: workspace?.id,
      search: debouncedSearch || undefined,
      filters:
        debouncedFilters.length > 0
          ? serializeFiltersToQuery(debouncedFilters)
          : undefined,
      sorts:
        debouncedSorts.length > 0
          ? serializeSortsToQuery(debouncedSorts)
          : undefined,
      from: dateRange.from
        ? dayjs(dateRange.from).startOf("month").format("YYYY-MM-DD")
        : undefined,
      to:
        (dateRange.to ?? dateRange.from)
          ? dayjs(dateRange.to ?? dateRange.from)
              .endOf("month")
              .format("YYYY-MM-DD")
          : undefined
    }),
    [
      workspace?.id,
      debouncedSearch,
      debouncedFilters,
      debouncedSorts,
      dateRange.from,
      dateRange.to
    ]
  );

  const { data: jobDataReal, isLoading: isLoadingJobs } = useGetData(
    GetJobScheduleByJob(queryParams)
  );

  const jobItemsRaw = useMemo(
    () => jobDataReal?.schedules ?? (jobDataReal as any)?.Schedules ?? [],
    [jobDataReal]
  );
  // Backend already resolves EDD ?? EstimatedEndDate into eddDate (WOR-2187).
  const jobItems = jobItemsRaw;

  const { data: technicianDataReal, isLoading: isLoadingTechnicians } =
    useGetData(GetJobScheduleByTechnician(queryParams));

  const technicianItems = useMemo(
    () =>
      technicianDataReal?.schedules ??
      (technicianDataReal as any)?.Schedules ??
      [],
    [technicianDataReal]
  );

  const { data: machineDataReal, isLoading: isLoadingMachines } = useGetData(
    GetJobScheduleByMachine(queryParams)
  );
  const machineItems = useMemo(
    () =>
      machineDataReal?.schedules ?? (machineDataReal as any)?.Schedules ?? [],
    [machineDataReal]
  );

  const { data: technicianMasterData } = useGetData(
    TechnicianList({
      page: 1,
      pageSize: 100,
      workspaceId: workspace?.id ?? 0,
      search: debouncedSearch || undefined
    }),
    { enabled: !!workspace?.id }
  );

  const technicianMasterList = useMemo(
    () => technicianMasterData?.data ?? [],
    [technicianMasterData]
  );

  const { data: machineMasterData } = useGetData(
    MachineListGroupedByShop({
      page: 1,
      pageSize: 100,
      workspaceId: workspace?.id ?? 0,
      search: debouncedSearch || undefined
    }),
    { enabled: !!workspace?.id }
  );

  const machineMasterList = useMemo(
    () =>
      (machineMasterData?.data ?? []).flatMap(
        (shop: any) => shop.machines ?? []
      ),
    [machineMasterData]
  );

  const technicianItemsEffective = useMemo(() => {
    if (technicianItems.length > 0) {
      return technicianItems;
    }
    return fallbackTechnicianItemsFromJobs(jobItems);
  }, [technicianItems, jobItems]);

  const machineItemsEffective = useMemo(() => {
    if (machineItems.length > 0) {
      return machineItems;
    }
    return fallbackMachineItemsFromJobs(jobItems);
  }, [machineItems, jobItems]);

  const jobFilterableAttributes: FilterableAttribute[] = [
    {
      id: "jobOrderNo",
      field: "jobOrderNo",
      label: "Job Order No",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "partNumber",
      field: "partNumber",
      label: "Part Number",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "partDescription",
      field: "partDescription",
      label: "Part Description",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "customerName",
      field: "customerName",
      label: "Customer Name",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "processName",
      field: "processName",
      label: "Process",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "technicianName",
      field: "technicianName",
      label: "Technician",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "machineName",
      field: "machineName",
      label: "Machine",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "status",
      field: "status",
      label: "Status",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "startDate",
      field: "startDate",
      label: "Start Date",
      dataType: AttributeDataType.DATETIME
    },
    {
      id: "eddDate",
      field: "eddDate",
      label: "EDD Date",
      dataType: AttributeDataType.DATETIME
    }
  ];

  const technicianFilterableAttributes: FilterableAttribute[] = [
    {
      id: "technicianName",
      field: "technicianName",
      label: "Technician",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "jobOrderNo",
      field: "jobOrderNo",
      label: "Job Order No",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "processName",
      field: "processName",
      label: "Process",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "machineName",
      field: "machineName",
      label: "Machine",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "status",
      field: "status",
      label: "Status",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "startTime",
      field: "startTime",
      label: "Start Time",
      dataType: AttributeDataType.DATETIME
    },
    {
      id: "endTime",
      field: "endTime",
      label: "End Time",
      dataType: AttributeDataType.DATETIME
    }
  ];

  const machineFilterableAttributes: FilterableAttribute[] = [
    {
      id: "machineName",
      field: "machineName",
      label: "Machine",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "shopName",
      field: "shopName",
      label: "Shop",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "jobOrderNo",
      field: "jobOrderNo",
      label: "Job Order No",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "processName",
      field: "processName",
      label: "Process",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "technicianName",
      field: "technicianName",
      label: "Technician",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "status",
      field: "status",
      label: "Status",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "startTime",
      field: "startTime",
      label: "Start Time",
      dataType: AttributeDataType.DATETIME
    },
    {
      id: "endTime",
      field: "endTime",
      label: "End Time",
      dataType: AttributeDataType.DATETIME
    }
  ];

  // Define sortable attributes for each tab
  const jobSortableAttributes: SortableAttribute[] = [
    { id: "jobOrderNo", field: "jobOrderNo", label: "Job Order No" },
    { id: "partNumber", field: "partNumber", label: "Part Number" },
    {
      id: "partDescription",
      field: "partDescription",
      label: "Part Description"
    },
    { id: "customerName", field: "customerName", label: "Customer Name" },
    { id: "processName", field: "processName", label: "Process" },
    { id: "technicianName", field: "technicianName", label: "Technician" },
    { id: "machineName", field: "machineName", label: "Machine" },
    { id: "priority", field: "priority", label: "Priority" },
    { id: "status", field: "status", label: "Status" },
    { id: "startDate", field: "startDate", label: "Start Date" },
    { id: "eddDate", field: "eddDate", label: "EDD Date" }
  ];

  const technicianSortableAttributes: SortableAttribute[] = [
    { id: "technicianName", field: "technicianName", label: "Technician" },
    { id: "jobOrderNo", field: "jobOrderNo", label: "Job Order No" },
    { id: "processName", field: "processName", label: "Process" },
    { id: "machineName", field: "machineName", label: "Machine" },
    { id: "priority", field: "priority", label: "Priority" },
    { id: "status", field: "status", label: "Status" },
    { id: "startTime", field: "startTime", label: "Start Time" },
    { id: "endTime", field: "endTime", label: "End Time" }
  ];

  const machineSortableAttributes: SortableAttribute[] = [
    { id: "machineName", field: "machineName", label: "Machine" },
    { id: "shopName", field: "shopName", label: "Shop" },
    { id: "jobOrderNo", field: "jobOrderNo", label: "Job Order No" },
    { id: "processName", field: "processName", label: "Process" },
    { id: "technicianName", field: "technicianName", label: "Technician" },
    { id: "priority", field: "priority", label: "Priority" },
    { id: "status", field: "status", label: "Status" },
    { id: "startTime", field: "startTime", label: "Start Time" },
    { id: "endTime", field: "endTime", label: "End Time" }
  ];

  // Transform backend data to component-friendly format
  const scheduledJobs = useMemo(
    () => transformToScheduledJobs(jobItems),
    [jobItems]
  );

  const technicianSchedules = useMemo(() => {
    // Always start from the full master list so every technician is visible,
    // even those without assignments in the current date range.
    if (technicianMasterList.length > 0) {
      return buildTechnicianScheduleFromMasterAndJobs(
        technicianMasterList,
        jobItems
      );
    }

    if (technicianItems.length > 0) {
      return transformToTechnicianSchedule(technicianItems);
    }

    return transformToTechnicianSchedule(technicianItemsEffective);
  }, [
    technicianItems,
    technicianMasterList,
    jobItems,
    technicianItemsEffective
  ]);

  const machineSchedules = useMemo(() => {
    // Always start from the full master list so every machine is visible,
    // even those without assignments in the current date range.
    if (machineMasterList.length > 0) {
      return buildMachineScheduleFromMasterAndJobs(machineMasterList, jobItems);
    }

    if (machineItems.length > 0) {
      return transformToMachineSchedule(machineItems);
    }

    return transformToMachineSchedule(machineItemsEffective);
  }, [machineItems, machineMasterList, jobItems, machineItemsEffective]);

  const technicianSchedulesWithJobs = useMemo(
    () => technicianSchedules.filter((item) => item.assignedJobs.length > 0),
    [technicianSchedules]
  );

  const machineSchedulesWithJobs = useMemo(
    () => machineSchedules.filter((item) => item.assignedJobs.length > 0),
    [machineSchedules]
  );

  // Backend handles search, filter, and sort - just use transformed data
  const filteredAndSortedJobs = scheduledJobs;

  const filteredAndSortedTechnicians = showTechnicianWithJobsOnly
    ? technicianSchedulesWithJobs
    : technicianSchedules;

  const filteredAndSortedMachines = showMachineWithJobsOnly
    ? machineSchedulesWithJobs
    : machineSchedules;

  const technicianTabCount = filteredAndSortedTechnicians.length;
  const machineTabCount = filteredAndSortedMachines.length;

  // Statistics
  const stats = {
    totalJobs: jobDataReal?.total ?? (jobDataReal as any)?.Total ?? 0,
    totalTechnicians:
      technicianDataReal?.total ??
      (technicianDataReal as any)?.Total ??
      technicianSchedules.length,
    totalMachines:
      machineDataReal?.total ??
      (machineDataReal as any)?.Total ??
      machineSchedules.length
  };

  const tabs: TabItemProps[] = [
    {
      value: "job",
      label: "Job",
      icon: (
        <div className="aero:flex aero:items-center aero:justify-center aero:gap-2 aero:text-[10px] aero:h-[22px] aero:w-[22px] aero:!rounded-full aero:bg-brand-primary-600">
          <div className="aero:text-white">{stats.totalJobs}</div>
        </div>
      ),
      content: (
        <JobGanttChart
          jobs={filteredAndSortedJobs}
          isLoading={isLoadingJobs}
          searchValue={search}
          onSearchChange={setSearch}
          filterableAttributes={jobFilterableAttributes}
          dynamicFilters={filters}
          onAddFilter={addFilter}
          onUpdateFilter={updateFilter}
          onRemoveFilter={removeFilter}
          onClearFilters={clearFilters}
          sortableAttributes={jobSortableAttributes}
          dynamicSorts={sorts}
          onAddSort={addSort}
          onUpdateSort={updateSort}
          onRemoveSort={removeSort}
          onClearSorts={clearSorts}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          zoomLevel={zoomLevel}
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
        />
      )
    },
    {
      value: "technician",
      label: "Technician",
      icon: (
        <div className="aero:flex aero:items-center aero:justify-center aero:gap-2 aero:text-[10px] aero:h-[22px] aero:w-[22px] aero:!rounded-full aero:bg-brand-primary-600">
          <div className="aero:text-white"> {technicianTabCount}</div>
        </div>
      ),
      content: (
        <TechnicianGanttChart
          technicians={filteredAndSortedTechnicians}
          isLoading={isLoadingTechnicians}
          searchValue={search}
          onSearchChange={setSearch}
          filterableAttributes={technicianFilterableAttributes}
          dynamicFilters={filters}
          onAddFilter={addFilter}
          onUpdateFilter={updateFilter}
          onRemoveFilter={removeFilter}
          onClearFilters={clearFilters}
          sortableAttributes={technicianSortableAttributes}
          dynamicSorts={sorts}
          onAddSort={addSort}
          onUpdateSort={updateSort}
          onRemoveSort={removeSort}
          onClearSorts={clearSorts}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          zoomLevel={zoomLevel}
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
        />
      )
    },
    {
      value: "machine",
      label: "Machine",
      icon: (
        <div className="aero:flex aero:items-center aero:justify-center aero:gap-2 aero:text-[10px] aero:h-[22px] aero:w-[22px] aero:!rounded-full aero:bg-brand-primary-600">
          <div className="aero:text-white"> {machineTabCount}</div>
        </div>
      ),
      content: (
        <>
          <MachineGanttChart
            machines={filteredAndSortedMachines}
            isLoading={isLoadingMachines}
            searchValue={search}
            onSearchChange={setSearch}
            filterableAttributes={machineFilterableAttributes}
            dynamicFilters={filters}
            onAddFilter={addFilter}
            onUpdateFilter={updateFilter}
            onRemoveFilter={removeFilter}
            onClearFilters={clearFilters}
            sortableAttributes={machineSortableAttributes}
            dynamicSorts={sorts}
            onAddSort={addSort}
            onUpdateSort={updateSort}
            onRemoveSort={removeSort}
            onClearSorts={clearSorts}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            zoomLevel={zoomLevel}
            onZoomIn={onZoomIn}
            onZoomOut={onZoomOut}
          />
        </>
      )
    }
  ];

  return (
    <div className="aero:flex aero:flex-col aero:gap-3 aero:w-full aero:max-w-full aero:overflow-hidden">
      {/* Header */}
      <div className="aero:flex aero:items-center aero:justify-between aero:flex-wrap aero:gap-4 aero:mb-4">
        <div>
          <h1 className="aero:text-2xl aero:font-bold">Processes</h1>
          <p className="aero:text-muted-foreground aero:text-sm">
            View and manage job assignments with Gantt charts
          </p>
        </div>
        <div className="aero:flex aero:items-center aero:gap-2">
          <Button
            intent="primary"
            onClick={() => setIsPlanningDrawerOpen(true)}
          >
            Plan with AI
          </Button>
          <WorkspaceToggle />
        </div>
      </div>

      <TabsComponent
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        scrollable={false}
        swapIconPosition
      />

      <AiPlanningDrawer
        open={isPlanningDrawerOpen}
        onOpenChange={setIsPlanningDrawerOpen}
        workspaceId={workspace?.id}
        filterableAttributes={jobFilterableAttributes}
        sortableAttributes={jobSortableAttributes}
      />
    </div>
  );
}
